import { ApiError, describeError, FormError, useApi } from '@classgo/app-kit';
import type { TeacherListItem, TimetableBlock, TimetableConflict } from '@classgo/shared';
import { Button, Input, Select } from '@classgo/ui';
import { type FormEvent, useState } from 'react';

import type { ClassroomView, SubjectView } from '../../academic/types';
import { useT } from '../../i18n';
import { timetableApi } from '../../timetable/api';
import { addMinutes, durationOf, minutesOf, teacherName, weekdayOf, WEEKDAYS } from './support';
import type { Slot } from './types';

/**
 * The conflicts a 409 carries, or none when the details are not what the API documents.
 */
const conflictsIn = (details: Readonly<Record<string, unknown>> | null): TimetableConflict[] => {
  const list = details?.['conflicts'];
  return Array.isArray(list) ? (list as TimetableConflict[]) : [];
};

const holdsAssignment = (teacher: TeacherListItem, groupId: string, subjectId: string) =>
  (teacher.assignments ?? []).some(
    (assignment) => assignment.groupId === groupId && assignment.subjectId === subjectId,
  );

/**
 * The dialog body that places or moves a block of a group's week: subject, teacher (the ones
 * assigned to that subject in the group first), classroom of the sede, day and hours. A refused
 * write stays on screen with what was typed: the API's message and, on a 409, the blocks it
 * collides with, so the coordinator changes the day, the hour, the teacher or the classroom and
 * saves again. "Guardar y agregar otro" keeps the dialog open at the hour the block ends.
 */
export const BlockForm = ({
  groupId,
  block,
  slot,
  subjects,
  teachers,
  classrooms,
  onSaved,
  onConflict,
  onDelete,
}: {
  groupId: string;
  /**
   * The block being edited; `null` for a new one.
   */
  block: TimetableBlock | null;
  /**
   * Where a new block starts.
   */
  slot: Slot;
  subjects: readonly SubjectView[];
  teachers: readonly TeacherListItem[];
  classrooms: readonly ClassroomView[];
  /**
   * After a write went through; `another` means the dialog stays open for the next block.
   */
  onSaved: (saved: TimetableBlock, another: boolean) => void;
  onConflict: (conflicts: TimetableConflict[]) => void;
  /**
   * Offered when editing: hands the block over to the removal confirmation.
   */
  onDelete?: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const [draft, setDraft] = useState(() =>
    block
      ? {
          subjectId: block.subjectId,
          teacherId: block.teacherId ?? '',
          classroomId: block.classroomId ?? '',
          weekday: weekdayOf(block.weekday),
          startsAt: block.startsAt,
          endsAt: block.endsAt,
        }
      : { subjectId: '', teacherId: '', classroomId: '', ...slot },
  );
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<TimetableConflict[]>([]);
  const [busy, setBusy] = useState<'save' | 'another' | null>(null);

  const assigned = teachers.filter((teacher) => holdsAssignment(teacher, groupId, draft.subjectId));
  const others = teachers.filter((teacher) => !assigned.includes(teacher));
  const option = (teacher: TeacherListItem) => ({ value: teacher.id, label: teacherName(teacher) });
  const teacherOptions = [
    { value: '', label: t('timetable.field.teacher.none') },
    ...(assigned.length > 0
      ? [{ value: 'assigned', label: t('timetable.field.teacher.assigned'), disabled: true }]
      : []),
    ...assigned.map(option),
    ...(assigned.length > 0 && others.length > 0
      ? [{ value: 'others', label: t('timetable.field.teacher.others'), disabled: true }]
      : []),
    ...others.map(option),
  ];

  /**
   * A subject change picks its only assigned teacher, keeps a still-assigned one, else clears.
   */
  const pickSubject = (subjectId: string) => {
    const fits = teachers.filter((teacher) => holdsAssignment(teacher, groupId, subjectId));
    setDraft((current) => ({
      ...current,
      subjectId,
      teacherId:
        fits.length === 1
          ? (fits[0]?.id ?? '')
          : fits.some((teacher) => teacher.id === current.teacherId)
            ? current.teacherId
            : '',
    }));
  };

  const submit = async (another: boolean) => {
    if (!draft.subjectId) {
      setError(t('timetable.incomplete'));
      return;
    }
    if (!draft.startsAt || !draft.endsAt || minutesOf(draft.startsAt) >= minutesOf(draft.endsAt)) {
      setError(t('timetable.hours'));
      return;
    }
    setBusy(another ? 'another' : 'save');
    setError(null);
    setConflicts([]);
    const body = {
      groupId,
      subjectId: draft.subjectId,
      teacherId: draft.teacherId || null,
      classroomId: draft.classroomId || null,
      weekday: draft.weekday,
      startsAt: draft.startsAt,
      endsAt: draft.endsAt,
    };
    try {
      const saved = block
        ? await timetableApi(api).update(block.id, body)
        : await timetableApi(api).create(body);
      if (another) {
        const length = durationOf(draft);
        setDraft((current) => ({
          ...current,
          startsAt: current.endsAt,
          endsAt: addMinutes(current.endsAt, length),
        }));
      }
      onSaved(saved, another);
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.code === 'CONFLICT') {
        const found = conflictsIn(thrown.details);
        setConflicts(found);
        setError(thrown.message);
        onConflict(found);
      } else if (
        thrown instanceof ApiError &&
        thrown.code === 'VALIDATION_FAILED' &&
        !thrown.message.startsWith('Invalid ')
      ) {
        // The API's own refusals are written for the coordinator (a teacher without the
        // assignment, a classroom of another sede); the schema's are not.
        setError(thrown.message);
      } else {
        setError(describeError(t, thrown));
      }
    } finally {
      setBusy(null);
    }
  };

  const dayName = (weekday: number) => t(`timetable.day.${weekdayOf(weekday)}`).toLowerCase();

  return (
    <form
      noValidate
      className="space-y-4"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void submit(false);
      }}
    >
      {conflicts.length > 0 ? (
        <div
          role="alert"
          className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
        >
          <p className="font-medium">{error}</p>
          <ul className="mt-2 space-y-1">
            {conflicts.map((conflict, index) => (
              <li key={`${conflict.kind}-${conflict.block.id}-${index}`}>
                {t(`timetable.conflict.${conflict.kind}`)}:{' '}
                {t('timetable.conflict.block', {
                  subject: conflict.block.subjectName,
                  group: conflict.block.groupName,
                  day: dayName(conflict.block.weekday),
                  start: conflict.block.startsAt,
                  end: conflict.block.endsAt,
                })}
                {conflict.block.teacherName ? ` · ${conflict.block.teacherName}` : ''}
                {conflict.block.classroomName ? ` · ${conflict.block.classroomName}` : ''}
              </li>
            ))}
          </ul>
          <p className="mt-2">{t('timetable.conflict.hint')}</p>
        </div>
      ) : (
        <FormError message={error} />
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('timetable.field.subject')}
          value={draft.subjectId}
          onChange={(event) => pickSubject(event.target.value)}
          required
          options={[
            { value: '', label: t('assignments.pick') },
            ...subjects.map((subject) => ({ value: subject.id, label: subject.name })),
          ]}
        />
        <Select
          label={t('timetable.field.teacher')}
          value={draft.teacherId}
          onChange={(event) =>
            setDraft((current) => ({ ...current, teacherId: event.target.value }))
          }
          options={teacherOptions}
          help={t('timetable.field.teacher.help')}
        />
        <Select
          label={t('timetable.field.classroom')}
          value={draft.classroomId}
          onChange={(event) =>
            setDraft((current) => ({ ...current, classroomId: event.target.value }))
          }
          options={[
            { value: '', label: t('timetable.field.classroom.none') },
            ...classrooms.map((classroom) => ({ value: classroom.id, label: classroom.name })),
          ]}
        />
        <Select
          label={t('timetable.field.weekday')}
          value={String(draft.weekday)}
          onChange={(event) =>
            setDraft((current) => ({ ...current, weekday: weekdayOf(Number(event.target.value)) }))
          }
          options={WEEKDAYS.map((day) => ({
            value: String(day),
            label: t(`timetable.day.${day}`),
          }))}
        />
        <Input
          label={t('timetable.field.startsAt')}
          type="time"
          step={300}
          required
          value={draft.startsAt}
          onChange={(event) =>
            setDraft((current) => ({ ...current, startsAt: event.target.value }))
          }
        />
        <Input
          label={t('timetable.field.endsAt')}
          type="time"
          step={300}
          required
          value={draft.endsAt}
          onChange={(event) => setDraft((current) => ({ ...current, endsAt: event.target.value }))}
        />
      </div>
      <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-end">
        {onDelete ? (
          <Button variant="danger" onClick={onDelete} className="sm:mr-auto">
            {t('timetable.delete')}
          </Button>
        ) : null}
        {block ? null : (
          <Button
            variant="secondary"
            loading={busy === 'another'}
            disabled={busy === 'save'}
            onClick={() => void submit(true)}
          >
            {t('timetable.saveAndNext')}
          </Button>
        )}
        <Button type="submit" loading={busy === 'save'} disabled={busy === 'another'}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};
